/**
 * Version history panel — a real, commit-backed timeline for one entity.
 * `publishManifest` records a bounded `manifestHistory` entry on every
 * publish; the `entityHistory` callable diffs consecutive snapshots and
 * returns only the entries that touched this entity. Presentational —
 * `entries` and `loading` are injected, matching AnnotationsPanel's split.
 */
import type { EntityHistoryEntry } from '../state/atlasApiTypes.js';
import { Icon } from '../components/Icon.js';

export interface EntityHistoryPanelProps {
  entityId: string;
  entries: EntityHistoryEntry[];
  loading: boolean;
}

/** Absolute, locale-formatted date for a history entry. */
export function formatHistoryDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** First 7 chars of a commit SHA — full string if already shorter. */
export function shortCommit(commit: string): string {
  return commit.length <= 7 ? commit : commit.slice(0, 7);
}

export function EntityHistoryPanel({
  entityId,
  entries,
  loading,
}: EntityHistoryPanelProps): React.JSX.Element {
  return (
    <section>
      <div className="section-label">Version history · last {entries.length || 0} tracked changes</div>
      {loading ? (
        <p style={{ color: 'var(--text3)' }}>Loading history…</p>
      ) : entries.length === 0 ? (
        <p style={{ color: 'var(--text3)' }}>
          No changes in the tracked publish history for {entityId}.
        </p>
      ) : (
        <div className="version-timeline">
          {entries.map((entry) => (
            <div key={entry.commit} className="version-entry">
              <Icon
                name={entry.kind === 'added' ? 'add_circle' : 'edit'}
                size="sm"
                color={entry.kind === 'added' ? 'var(--good)' : 'var(--caution)'}
              />
              <span className="mono">{shortCommit(entry.commit)}</span>
              <span className="ref-kind">{entry.kind}</span>
              <span style={{ color: 'var(--text3)' }}>{formatHistoryDate(entry.generatedAt)}</span>
              {entry.fieldChanges.length > 0 && (
                <ul className="enrichment-list enrichment-list--mono">
                  {entry.fieldChanges.map((fc) => (
                    <li key={fc.field}>
                      {fc.field} — {fc.change}
                      {fc.from && fc.to ? ` (${fc.from} → ${fc.to})` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
